import { RefObject } from 'react';
import {
    Box, Chip, IconButton, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import MDButton from '@components/MDButton';
import { ExportButton } from '@components/ExportButton';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { TagResponse } from '@layouts/pages/Settings/general/tags/services/responses/tag.response';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { BoardDetailResponse } from '../../services/responses/board.response';
import { getBoardColor, getCrmColors } from '../../crm-theme';
import { ScheduleFilter } from '../../filters/crm-schedule-filter';
import { CrmKanbanView } from '../../crm-kanban-view';
import { hasLeadFilters } from '../../filters/crm-lead-filters';
import { CrmBoardFilters } from './crm-board-filters';
import { CrmBoardMetrics } from './crm-board-metrics';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';
import {
    CONTROLS_SX, TOP_ROW_SX, barSx, boardColorSx, boardNameSx, iconButtonSx, pickerButtonSx, pickerLabelSx,
    searchFieldSx, viewToggleSx,
} from './crm-board-bar.styles';

interface Props {
    barRef: RefObject<HTMLDivElement>;
    board: BoardDetailResponse | null;
    statuses: LeadStatusResponse[];
    searchTerm: string;
    onSearchTermChange: (term: string) => void;
    statusFilterId: string | null;
    onStatusFilterChange: (statusId: string | null) => void;
    scheduleFilter: ScheduleFilter | null;
    onScheduleFilterChange: (filter: ScheduleFilter | null) => void;
    users: UserResponse[];
    /// Id do responsável, OWNER_UNASSIGNED ou nulo para todos.
    ownerFilter: string | null;
    onOwnerFilterChange: (owner: string | null) => void;
    tags: TagResponse[];
    /// Id da tag ou nulo para todas.
    tagFilter: string | null;
    onTagFilterChange: (tagId: string | null) => void;
    onClearFilters: () => void;
    onOpenPicker: (anchor: HTMLElement) => void;
    showClosed: boolean;
    onToggleClosed: () => void;
    view: CrmKanbanView;
    onViewChange: (view: CrmKanbanView) => void;
    /// Planilha com os leads que os filtros mostram.
    onExport: () => void;
    exporting: boolean;
}

export function CrmBoardBar({
    barRef, board, statuses, searchTerm, onSearchTermChange, statusFilterId, onStatusFilterChange,
    scheduleFilter, onScheduleFilterChange, users, ownerFilter, onOwnerFilterChange, tags, tagFilter,
    onTagFilterChange, onClearFilters, onOpenPicker, showClosed, onToggleClosed, view, onViewChange,
    onExport, exporting,
}: Props) {
    const { t } = useTranslation();
    const { canExportLeads } = useProfilePermissions();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const filters = { searchTerm, statusFilterId, ownerFilter, tagFilter, scheduleFilter };
    const hasFilters = hasLeadFilters(filters);

    return (
        <Box ref={barRef} sx={barSx(cc)}>
            <Box sx={TOP_ROW_SX}>
                {board && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0, flex: '1 1 220px' }}>
                        <Box sx={boardColorSx(getBoardColor(board.id))} />
                        <Typography sx={boardNameSx(cc)}>{board.name}</Typography>
                        <Chip
                            label={board.active ? t('crmKanban.ativo') : t('crmKanban.fechado')}
                            size="small"
                            color={board.active ? 'success' : 'default'}
                            sx={{ fontWeight: 600, fontSize: 11, flexShrink: 0 }}
                        />
                    </Box>
                )}

                <Box sx={CONTROLS_SX}>
                    {canExportLeads && (
                        <ExportButton title={t('exportar.leads')} onClick={onExport} busy={exporting} disabled={!board} />
                    )}

                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={view}
                        onChange={(_, value: CrmKanbanView | null) => value && onViewChange(value)}
                        aria-label={t('crmLista.visao')}
                        sx={viewToggleSx(cc)}
                    >
                        <ToggleButton value="board">
                            <ViewKanbanIcon fontSize="small" />
                            {t('crmLista.visaoQuadro')}
                        </ToggleButton>
                        <ToggleButton value="list">
                            <ViewListIcon fontSize="small" />
                            {t('crmLista.visaoLista')}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <TextField
                        size="small"
                        placeholder={t('crmKanban.buscarLead')}
                        value={searchTerm}
                        onChange={(e) => onSearchTermChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: cc.textMuted }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={searchFieldSx(cc)}
                    />

                    <CrmBoardFilters
                        users={users}
                        ownerFilter={ownerFilter}
                        onOwnerFilterChange={onOwnerFilterChange}
                        tags={tags}
                        tagFilter={tagFilter}
                        onTagFilterChange={onTagFilterChange}
                        scheduleFilter={scheduleFilter}
                        onScheduleFilterChange={onScheduleFilterChange}
                    />

                    <Tooltip title={showClosed ? t('crmKanban.esconderEncerrados') : t('crmKanban.mostrarEncerrados')}>
                        <IconButton onClick={onToggleClosed} sx={iconButtonSx(cc, showClosed, cc.textSecondary)}>
                            <TaskAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('crmKanban.limparFiltros')}>
                        {/* O span segura o Tooltip quando o botão está desabilitado. */}
                        <span>
                            <IconButton
                                onClick={onClearFilters}
                                disabled={!hasFilters}
                                sx={iconButtonSx(cc, false, hasFilters ? cc.textPrimary : cc.textMuted)}
                            >
                                <FilterAltOffIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Box sx={{ width: '1px', height: 30, bgcolor: cc.border }} />

                    <MDButton
                        variant="outlined"
                        onClick={(e) => onOpenPicker(e.currentTarget)}
                        startIcon={<BackupTableIcon sx={{ color: cc.textPrimary }} />}
                        endIcon={<ExpandMoreIcon sx={{ color: cc.textPrimary }} />}
                        sx={pickerButtonSx(cc)}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                            <Typography variant="caption" sx={pickerLabelSx(cc)}>{t('crmKanban.quadro')}</Typography>
                            <Typography
                                variant="body2"
                                noWrap
                                maxWidth={160}
                                sx={{ fontSize: '13px', fontWeight: 700, color: cc.textPrimary }}
                            >
                                {board?.name ?? '-'}
                            </Typography>
                        </Box>
                    </MDButton>
                </Box>
            </Box>

            {board && (
                <CrmBoardMetrics
                    board={board}
                    statuses={statuses}
                    filters={filters}
                    hasFilters={hasFilters}
                    statusFilterId={statusFilterId}
                    onStatusFilterChange={onStatusFilterChange}
                />
            )}
        </Box>
    );
}
